import React, { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message,
      });
      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error('Contact submission error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="page-transition">
      {/* Hero Section */}
      <section className="pt-32 pb-12 sm:pt-40 sm:pb-16">
        <div className="container-main">
          <div className="max-w-2xl">
            <p className="label-text mb-6 animate-fade-in">Contact Us</p>
            <h1 className="heading-1 mb-6 animate-slide-up">
              Start a
              <br />
              conversation.
            </h1>
            <p className="body-text text-lg animate-slide-up stagger-2">
              Whether you have a specific challenge in mind or just want to explore
              how we might help, we'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="pb-20 sm:pb-28">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left Column - Contact Info */}
            <div className="lg:col-span-4">
              <div className="sticky top-32">
                <h2 className="heading-3 mb-6">Get in Touch</h2>
                <p className="body-text mb-8">
                  We typically respond within one business day. For urgent matters,
                  please reach out directly via email.
                </p>
                
                <div className="space-y-6">
                  <div>
                    <p className="label-text mb-2">Email</p>
                    <a
                      href="mailto:pdorsey@dorseyandassociates.com"
                      data-testid="contact-email"
                      className="text-slate-900 font-medium hover:underline break-all"
                    >
                      pdorsey@dorseyandassociates.com
                    </a>
                  </div>
                  <div>
                    <p className="label-text mb-2">LinkedIn</p>
                    <a
                      href="https://www.linkedin.com/in/patdorsey/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-linkedin"
                      className="text-slate-900 font-medium hover:underline flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      Pat Dorsey
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="lg:col-span-8">
              {isSubmitted ? (
                <div
                  data-testid="success-message"
                  className="bg-gray-50 p-12 sm:p-16 text-center"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="heading-2 mb-4">Message Sent</h2>
                  <p className="body-text text-lg mb-8">
                    Thank you for reaching out. We'll be in touch soon.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    data-testid="send-another-btn"
                    className="btn-secondary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="label-text block mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="input-name"
                        className="form-input"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="label-text block mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="input-email"
                        className="form-input"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="label-text block mb-2">
                      Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="input-phone"
                      className="form-input"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="label-text block mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      data-testid="input-message"
                      className="form-textarea"
                      placeholder="Tell us about your challenge or question..."
                      rows={6}
                    />
                  </div>

                  {error && (
                    <div
                      data-testid="error-message"
                      className="text-red-600 text-sm"
                    >
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="submit-btn"
                    className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
